// Kilometerstand (ODO) på elsykkelservicer (backend: PATCH /workshop-services/:id/odo, src/lib/odo.js).
// OdoFelt står på servicen, OdoAdvarsel kommer når «Ferdig» trykkes uten ODO, og OdoLogg vises på
// sykkelkortet. Avlesningene ligger også i loggen på sykkelkortet (Bike.odoLogg), så «siden sist»
// regnes ut derfra.
import React, { useEffect, useId, useRef, useState } from 'react';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const MAKS_KM = 300000;

const fmtKm = (km) => `${Number(km).toLocaleString('nb-NO')} km`;
const fmtDato = (d) => (d ? new Date(d).toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

/** Elsykkelservice: tjenestegruppe el-sykkel eller motorservice, eller koblet sykkel merket elsykkel. */
export function erElsykkelService(svc, sykkel) {
  const type = String(svc?.produkt?.slug || svc?.produkt?.serviceSlug || svc?.produkt?.produkttype || '')
    .trim().toLowerCase().replace(/\s+/g, '-');
  return type === 'el-sykkel' || type === 'elsykkel' || type === 'motorservice' || sykkel?.elsykkel === true;
}

export const harOdo = (svc) => svc?.odo?.km !== null && svc?.odo?.km !== undefined;

/** Leser tallet fra feltet: '' = tomt, ellers et helt tall 0–300 000 (mellomrom tåles), eller { feil }. */
export function lesKmFelt(tekst) {
  const rå = String(tekst ?? '').replace(/[\s ]/g, '');
  if (!rå) return { km: null };
  if (!/^\d+$/.test(rå)) return { feil: 'Skriv kilometerstanden som et helt tall, f.eks. 1234.' };
  const km = Number(rå);
  if (km > MAKS_KM) return { feil: `Maks ${fmtKm(MAKS_KM)}. Sjekk tallet.` };
  return { km };
}

/** Siste avlesning på sykkelkortet før denne servicen (en annen service, ikke senere enn denne). */
export function forrigeAvlesning(odoLogg, serviceId, foer) {
  const grense = foer ? new Date(foer).getTime() : Infinity;
  return (odoLogg || [])
    .filter((r) => String(r.serviceId || '') !== String(serviceId || '') && new Date(r.dato).getTime() <= grense)
    .sort((a, b) => new Date(b.dato) - new Date(a.dato))[0] || null;
}

/** Lagrer ODO på servicen. Returnerer { odo, odoLogg } eller kaster med serverens melding. */
export async function lagreOdo(serviceId, km) {
  const r = await fetch(`${API_BASE_URL}/workshop-services/${serviceId}/odo`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ km, ansatt: Cookies.get('selectedEmployee') || '' }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.message || 'Kunne ikke lagre kilometerstanden');
  return data;
}

/** «Siden sist» for en avlesning, med forrige avlesning fra sykkelkortet. */
function SidenSist({ km, forrige }) {
  if (km === null || km === undefined || !forrige) return null;
  const diff = km - forrige.km;
  if (diff < 0) {
    return (
      <p className="mt-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-sm text-amber-900">
        Lavere enn forrige avlesning ({fmtKm(forrige.km)}, {fmtDato(forrige.dato)}). Sjekk tallet, eller noter
        i servicen om displayet eller motoren er byttet.
      </p>
    );
  }
  return (
    <p className="mt-1 text-sm text-slate-700">
      <strong className="text-emerald-800">{fmtKm(diff)}</strong> siden forrige besøk
      {' '}({fmtKm(forrige.km)}, {fmtDato(forrige.dato)}{forrige.serviceRef ? `, #${forrige.serviceRef}` : ''}).
    </p>
  );
}

/** ODO-feltet på servicen. */
export function OdoFelt({ svc, sykkel, onLagret }) {
  const id = useId();
  const [tekst, setTekst] = useState(harOdo(svc) ? String(svc.odo.km) : '');
  const [lagrer, setLagrer] = useState(false);
  const [melding, setMelding] = useState(null);

  useEffect(() => { setTekst(harOdo(svc) ? String(svc.odo.km) : ''); }, [svc?._id, svc?.odo?.km]); // eslint-disable-line react-hooks/exhaustive-deps

  const lagret = harOdo(svc) ? svc.odo.km : null;
  const lest = lesKmFelt(tekst);
  const endret = !lest.feil && lest.km !== lagret;
  const forrige = sykkel ? forrigeAvlesning(sykkel.odoLogg, svc?._id, svc?.odo?.registrertAt) : null;

  async function lagre(e) {
    e.preventDefault();
    if (lest.feil) { setMelding({ ok: false, tekst: lest.feil }); return; }
    setLagrer(true);
    setMelding(null);
    try {
      const data = await lagreOdo(svc._id, lest.km);
      onLagret?.(data);
      setMelding({ ok: true, tekst: lest.km === null ? 'Kilometerstanden er fjernet.' : 'Lagret.' });
    } catch (err) {
      setMelding({ ok: false, tekst: err.message });
    } finally {
      setLagrer(false);
    }
  }

  return (
    <section aria-labelledby={`${id}-tittel`} className="rounded-2xl border border-slate-200 bg-white p-3 mb-4">
      <h3 id={`${id}-tittel`} className="text-base font-bold text-slate-900">Kilometerstand (ODO)</h3>
      <form onSubmit={lagre} className="mt-2 flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor={`${id}-km`} className="block text-sm font-medium text-slate-700">ODO i km</label>
          <input
            id={`${id}-km`}
            inputMode="numeric"
            autoComplete="off"
            value={tekst}
            onChange={(e) => { setTekst(e.target.value); setMelding(null); }}
            aria-describedby={`${id}-hjelp`}
            aria-invalid={lest.feil ? 'true' : undefined}
            placeholder="f.eks. 1234"
            className="mt-1 w-40 rounded-lg border border-slate-300 px-3 py-2"
          />
        </div>
        <button
          type="submit"
          disabled={lagrer || !endret}
          className="min-h-[2.5rem] rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {lagrer ? 'Lagrer …' : 'Lagre ODO'}
        </button>
      </form>
      <p id={`${id}-hjelp`} className="mt-1 text-sm text-slate-600">
        Les av displayet ved hver service på elsykkel.
        {harOdo(svc) && svc.odo.registrertAt && (
          <> Registrert {fmtDato(svc.odo.registrertAt)}{svc.odo.registrertAv ? ` av ${svc.odo.registrertAv}` : ''}.</>
        )}
      </p>
      <div aria-live="polite">
        {melding && (
          <p className={`mt-1 text-sm font-semibold ${melding.ok ? 'text-emerald-800' : 'text-rose-800'}`}>{melding.tekst}</p>
        )}
      </div>
      {!sykkel ? (
        <p className="mt-1 text-sm text-slate-600">Koble sykkelkortet for å se hvor langt kunden har syklet siden sist.</p>
      ) : harOdo(svc) ? (
        <SidenSist km={svc.odo.km} forrige={forrige} />
      ) : forrige ? (
        <p className="mt-1 text-sm text-slate-700">Forrige avlesning: {fmtKm(forrige.km)} ({fmtDato(forrige.dato)}).</p>
      ) : (
        <p className="mt-1 text-sm text-slate-600">Ingen tidligere avlesning på sykkelkortet.</p>
      )}
    </section>
  );
}

/** Advarselen når «Ferdig» trykkes uten ODO. onFortsett kalles etter lagring eller «Fortsett uten». */
export function OdoAdvarsel({ svc, sykkel, onLagret, onFortsett, onAvbryt }) {
  const id = useId();
  const feltRef = useRef(null);
  const [tekst, setTekst] = useState('');
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const forrige = sykkel ? forrigeAvlesning(sykkel.odoLogg, svc?._id) : null;
  const lest = lesKmFelt(tekst);

  useEffect(() => { feltRef.current?.focus(); }, []);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onAvbryt(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onAvbryt]);

  async function lagreOgFortsett(e) {
    e.preventDefault();
    if (lest.feil || lest.km === null) { setFeil(lest.feil || 'Skriv inn kilometerstanden, eller velg «Fortsett uten ODO».'); return; }
    setLagrer(true);
    setFeil('');
    try {
      const data = await lagreOdo(svc._id, lest.km);
      onLagret?.(data);
      onFortsett();
    } catch (err) {
      setFeil(err.message);
      setLagrer(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
      <div role="dialog" aria-modal="true" aria-labelledby={`${id}-tittel`} aria-describedby={`${id}-tekst`}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <h2 id={`${id}-tittel`} className="text-base font-bold text-slate-900">Kilometerstand mangler</h2>
        <p id={`${id}-tekst`} className="mt-1 text-sm text-slate-700">
          Dette er en elsykkelservice. Registrer ODO fra displayet før servicen er ferdig.
          {forrige && <> Forrige avlesning: {fmtKm(forrige.km)} ({fmtDato(forrige.dato)}).</>}
        </p>
        <form onSubmit={lagreOgFortsett} className="mt-4">
          <label htmlFor={`${id}-km`} className="block text-sm font-medium text-slate-700">ODO i km</label>
          <input
            ref={feltRef}
            id={`${id}-km`}
            inputMode="numeric"
            autoComplete="off"
            value={tekst}
            onChange={(e) => { setTekst(e.target.value); setFeil(''); }}
            aria-invalid={feil ? 'true' : undefined}
            aria-describedby={feil ? `${id}-feil` : undefined}
            placeholder="f.eks. 1234"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
          />
          {!lest.feil && lest.km !== null && <SidenSist km={lest.km} forrige={forrige} />}
          {feil && <p id={`${id}-feil`} role="alert" className="mt-2 text-sm font-semibold text-rose-800">{feil}</p>}
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button type="button" onClick={onAvbryt} className="rounded-lg bg-slate-100 px-4 py-2 text-sm hover:bg-slate-200">
              Avbryt
            </button>
            <button type="button" onClick={onFortsett} className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50">
              Fortsett uten ODO
            </button>
            <button type="submit" disabled={lagrer} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {lagrer ? 'Lagrer …' : 'Lagre og fortsett'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Kilometerstanden på sykkelkortet: alle avlesninger, nyeste først, med kjørt siden forrige. */
export function OdoLogg({ odoLogg, onApneService }) {
  const rader = [...(odoLogg || [])].sort((a, b) => new Date(b.dato) - new Date(a.dato));
  if (!rader.length) {
    return <p className="text-sm text-slate-600">Ingen avlesninger ennå. ODO registreres på elsykkelservicer.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <caption className="sr-only">Kilometerstand fra servicene, nyeste først</caption>
        <thead className="bg-gray-100 text-gray-700">
          <tr>
            <th scope="col" className="px-3 py-2 text-left">Dato</th>
            <th scope="col" className="px-3 py-2 text-right">ODO</th>
            <th scope="col" className="px-3 py-2 text-right">Siden forrige</th>
            <th scope="col" className="px-3 py-2 text-left">Service</th>
          </tr>
        </thead>
        <tbody>
          {rader.map((r, i) => {
            const forrige = rader[i + 1];
            const diff = forrige ? r.km - forrige.km : null;
            return (
              <tr key={`${r.serviceId || i}`} className="border-t">
                <td className="px-3 py-2">{fmtDato(r.dato)}</td>
                <td className="px-3 py-2 text-right font-medium">{fmtKm(r.km)}</td>
                <td className={`px-3 py-2 text-right ${diff !== null && diff < 0 ? 'font-semibold text-amber-800' : 'text-slate-700'}`}>
                  {diff === null ? '–' : diff < 0 ? `${fmtKm(diff)} (sjekk)` : `+${fmtKm(diff)}`}
                </td>
                <td className="px-3 py-2">
                  {r.serviceId && onApneService ? (
                    <button type="button" onClick={() => onApneService(r.serviceId)} className="text-indigo-700 underline underline-offset-2">
                      {r.serviceRef ? `#${r.serviceRef}` : 'Åpne'}
                    </button>
                  ) : (r.serviceRef ? `#${r.serviceRef}` : '–')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
